import React from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { X, Zap, Check } from 'lucide-react';
import { TOPUP_PACKS, TopupPack } from '../services/plan';
import { useAuth } from '../contexts/AuthContext';
import { environment } from '../config/environment';

// TopupDialog — modal for buying non-expiring stacking-credit packs.
// Independent of the storage-tier subscription — anyone can buy a pack
// including free-tier users who've exhausted their monthly grant.
//
// Wire-up:
//   Buy button submits an HTML form (POST) to `${STRIPE_TOPUP_URL}/checkout`
//   with priceId + cognito_sub + email as fields. The lambda replies with
//   a 303 Location: <stripe-hosted-checkout-url>, and the browser follows
//   it as a top-level navigation. No CORS involved because form submits
//   don't require the response body to be readable. Requires
//   REACT_APP_STRIPE_TOPUP_URL and per-pack REACT_APP_STRIPE_PRICE_* env
//   vars to be set at build time.

interface Props {
  open: boolean;
  onClose: () => void;
  /** Currently-selected pack id — controlled from parent for keyboard nav. */
  initialSelectedId?: string;
}

const TopupDialog: React.FC<Props> = ({ open, onClose, initialSelectedId }) => {
  const { user } = useAuth();
  // Default to the middle pack (best per-1000 rate that's still an
  // impulse-buy price). Anchoring on the small pack undersells the
  // bulk savings; anchoring on the whale pack scares off casual users.
  const [selectedId, setSelectedId] = React.useState<string>(
    initialSelectedId ?? 'bulk',
  );

  const selectedPriceId = environment.stripe.priceIds[selectedId] || '';
  const checkoutBase = environment.stripe.topupBaseUrl;
  const canCheckout = !!(selectedPriceId && checkoutBase && user?.sub);
  const buyDisabledReason = !user?.sub
    ? 'sign in to buy credits'
    : !checkoutBase
    ? 'REACT_APP_STRIPE_TOPUP_URL not configured'
    : !selectedPriceId
    ? `no Stripe price ID configured for the "${selectedId}" pack`
    : '';

  // ESC to close. Matches the pattern in ConfirmDialog / FilterFramesDialog.
  React.useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') { e.preventDefault(); onClose(); }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, onClose]);

  return (
    <AnimatePresence>
      {open && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.15 }}
          className="fixed inset-0 z-50 bg-black/70 backdrop-blur-sm flex items-center justify-center p-4"
          onClick={onClose}
        >
          <motion.div
            initial={{ opacity: 0, y: 20, scale: 0.98 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 10, scale: 0.98 }}
            transition={{ duration: 0.2 }}
            className="relative w-full max-w-3xl max-h-[90vh] flex flex-col rounded-2xl bg-space-900 border border-white/10 shadow-2xl overflow-hidden"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Header */}
            <div className="shrink-0 flex items-start justify-between p-6 pb-4 border-b border-white/[0.06]">
              <div>
                <div className="flex items-center gap-2">
                  <div className="inline-flex h-8 w-8 rounded-lg bg-nebula-500/15 border border-nebula-400/30 items-center justify-center">
                    <Zap className="h-4 w-4 text-nebula-200" />
                  </div>
                  <h2 className="font-display text-xl font-bold">Add stacking credits</h2>
                </div>
                <p className="text-sm text-white/50 mt-2">
                  One-time purchase. Credits never expire. Stackable on top of
                  your monthly plan grant — buy once for the year if you like.
                </p>
              </div>
              <button
                onClick={onClose}
                className="p-2 rounded-lg text-white/60 hover:text-white hover:bg-white/[0.06] transition-colors"
                aria-label="Close"
              >
                <X className="h-4 w-4" />
              </button>
            </div>

            {/* Pack grid — scrollable middle so the footer CTA stays pinned
                on short (mobile) viewports where 3 stacked cards + header +
                footer exceeds 90vh. */}
            <div className="flex-1 min-h-0 overflow-y-auto p-6 grid gap-3 md:grid-cols-3">
              {TOPUP_PACKS.map((pack) => (
                <PackCard
                  key={pack.id}
                  pack={pack}
                  selected={selectedId === pack.id}
                  onSelect={() => setSelectedId(pack.id)}
                />
              ))}
            </div>

            {/* Footer with CTA */}
            <div className="shrink-0 border-t border-white/[0.06] p-6 pt-4 bg-white/[0.02]">
              <div className="flex items-center justify-between gap-4 flex-wrap">
                <div className="text-xs text-white/45 leading-relaxed max-w-md">
                  Non-drizzle: first 500 frames free, then 1 credit per
                  frame above 500. Drizzle: 2 credits per frame (larger
                  canvas, more compute). Unused credits carry over
                  indefinitely.
                </div>
                {/* Real HTML form submit — the lambda replies with a 303
                    redirect to Stripe's hosted checkout, and a top-level
                    form submission follows the redirect natively (unlike
                    fetch, which would need CORS + custom Location handling).
                    Fields match what handleCheckout() in the lambda reads. */}
                <form
                  method="POST"
                  action={canCheckout ? `${checkoutBase}/checkout` : undefined}
                >
                  <input type="hidden" name="priceId"      value={selectedPriceId} />
                  <input type="hidden" name="cognito_sub"  value={(user?.sub as string) || ''} />
                  <input type="hidden" name="email"        value={(user?.email as string) || ''} />
                  <button
                    type="submit"
                    disabled={!canCheckout}
                    title={canCheckout ? 'Continue to Stripe Checkout' : buyDisabledReason}
                    className="px-5 py-2.5 rounded-xl text-sm font-semibold bg-nebula-500 hover:bg-nebula-400 text-white shadow-nebula transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    Buy with Stripe
                  </button>
                </form>
              </div>
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

const PackCard: React.FC<{
  pack: TopupPack;
  selected: boolean;
  onSelect: () => void;
}> = ({ pack, selected, onSelect }) => (
  <button
    type="button"
    onClick={onSelect}
    className={`relative text-left flex flex-col p-5 rounded-xl border-2 transition-all ${
      selected
        ? 'bg-nebula-500/[0.08] border-nebula-400/70 shadow-nebula'
        : 'bg-white/[0.02] border-white/10 hover:bg-white/[0.04] hover:border-white/20'
    }`}
  >
    {selected && (
      <div className="absolute -top-2 right-3 px-2 py-0.5 rounded-full bg-nebula-500 text-white text-[10px] font-semibold uppercase tracking-wider flex items-center gap-1">
        <Check className="h-3 w-3" />
        selected
      </div>
    )}
    <div className="text-xs uppercase tracking-widest text-white/40 font-semibold">
      {pack.name}
    </div>
    <div className="mt-2 flex items-baseline gap-1">
      <span className="font-display text-3xl font-bold">${pack.priceUSD}</span>
    </div>
    <div className="text-xs text-white/50 mt-1 min-h-[2.5rem]">{pack.tagline}</div>
    <div className="mt-4 space-y-1.5 text-xs flex-1">
      <div className="flex items-center gap-1.5 text-white/70">
        <Check className="h-3 w-3 text-emerald-400/80 shrink-0" />
        {pack.frames.toLocaleString()} frames
      </div>
      <div className="flex items-center gap-1.5 text-white/70">
        <Check className="h-3 w-3 text-emerald-400/80 shrink-0" />
        ${pack.perThousand.toFixed(2)} per 1,000 frames
      </div>
      <div className="flex items-center gap-1.5 text-white/70">
        <Check className="h-3 w-3 text-emerald-400/80 shrink-0" />
        Never expires
      </div>
    </div>
  </button>
);

export default TopupDialog;
